import { HttpClient, HttpHeaders } from "@angular/common/http";
import { EventEmitter, Injectable, Injector } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, Subscription } from "rxjs";
import { environment } from "../../environments/environment";
import { CurrentService } from "./current.service";
import { DatasService } from "./datas.service";
import { MODAL, ModalService } from "./modal.service";
import { PrintService } from "./print.service";
import { Tools } from "./tools";
import { UserService } from "./user.service";

import * as HttpStatus from "http-status-codes";
import { BYME_MESSAGES } from "../enum/byme-messages";
import { CONTEXT } from "../enum/context";
import { MAGENTO_MESSAGES } from "../enum/magento-messages";
import { Stock } from "../model/stock";
import { EventDataTransferService } from "./event-data-transfer.service";
import { GTM } from "./gtm.service";
import { BomDataHelper } from "./helpers/bom-data-helper";
import { MagentoService } from "./magento.service";
import { ProjectService } from "./project.service";
import { ProjectDuplicate } from "../model/project-duplicate";

@Injectable({
  providedIn: "root",
})
export class BymeService {
  public iframe: HTMLIFrameElement;
  public byMeApiToken: string;
  public byMeApiTokenType: string;
  public projectToInit: any;
  public iframeParametersAtInit: any;
  public saveForOrderRequested: boolean;
  public saveForAccountCreationRequested: boolean;
  public loginForSaveRequested: boolean;
  public bom: any;
  public distribId: string;
  private _plan2DrenderingEventEmitter: EventEmitter<any>;
  private _saveForPrintRequested: boolean;

  constructor(
    private injector: Injector,
    private current: CurrentService,
    private customer: UserService,
    private datas: DatasService,
    private modalService: ModalService,
    private printService: PrintService,
    private http: HttpClient,
    private router: Router,
    private magento: MagentoService,
    private gtm: GTM,
    private eventDataTransfert: EventDataTransferService
  ) {
    window.addEventListener("message", this.handleIFrameMessage);
    this.iframeParametersAtInit = this.getByMeDefaultSettings();
    this._plan2DrenderingEventEmitter = new EventEmitter();
    this.modalService.closeEvents.subscribe((e: any) => {
      if (this.printService.hasContent()) {
        if (this.iframe) this.iframe.style.paddingBottom = "1px";
        window.setTimeout(() => {
          this.printService.reset();
          if (this.iframe) this.iframe.style.paddingBottom = "0";
        }, 50);
      }
    });

    this.customer.events.subscribe((action: string) => {
      console.log("CUSTOMER_EVENT");
      if (action == "CUSTOMER_LOGOUT") {
        this.byMeApiToken = null;
        this.byMeApiTokenType = null;
      } else if (action == "CUSTOMER_LOGIN") {
        this.acquireToken(this.customer.userDatas.hbmId).then(() => {
          // rien à faire de plus
        });
      }
    });
  }

  initService() {
    this.customer.byMeServiceRef = this; // evite le circular-dependency si customer import byme par injection
  }

  async initIframe(iframe: HTMLIFrameElement) {
    this.iframe = iframe;
    let params: any = await this.datas.getIFrameParameters();
    this.distribId = params.distribId;
    this.iframe.src =
      params.url +
      (this.current.project ? "&projectId=" + this.current.project.uuid : "");
  }

  prepareBymeSettingsForLoadingProject() {
    this.iframeParametersAtInit = this.getByMeDefaultSettings();
    this.iframeParametersAtInit.designFromScratch = false;
    this.iframeParametersAtInit.name = this.current.project.name;
  }

  async prepareBymeSettings(
    modelId: number,
    layoutRef: string,
    ovenConfigurationRef: string,
    furnitureConfiguration: string
  ) {
    this.current.project = null;
    this.iframeParametersAtInit = this.getByMeDefaultSettings();
    this.iframeParametersAtInit.designFromScratch = false;

    if (modelId > 0) {
      let data = await this.datas.getModels();
      let _model = data.find((m) => m.id === modelId);
      for (const key in _model.style) {
        this.iframeParametersAtInit[key] = _model.style[key];
      }
    }

    if (layoutRef) {
      this.iframeParametersAtInit.templateTags =
        this.iframeParametersAtInit.templateTags.filter((tag: any) => {
          return tag.required[0] == layoutRef;
        });
    }

    if (furnitureConfiguration) {
      this.iframeParametersAtInit.templateTags.map((tag: any) => {
        tag.required.splice(1, 0, furnitureConfiguration);
      });
    }

    if (ovenConfigurationRef) {
      this.iframeParametersAtInit.templateTags.map((tag: any) => {
        tag.required.splice(1, 0, ovenConfigurationRef);
      });
    }
  }

  async getAuthParams(
    hbmId: string,
    forceTokenRefresh: boolean = false
  ): Promise<{ token: string; distributionID: string }> {
    if (this.byMeApiToken && !forceTokenRefresh) {
      return {
        token: this.byMeApiToken,
        distributionID: this.distribId,
      };
    } else {
      await this.acquireToken(hbmId, false);
      return {
        token: this.byMeApiToken,
        distributionID: this.distribId,
      };
    }
  }

  onIFrameReady() {
    this.postSettingsToIFrame();

    if (this.byMeApiToken) {
      this.postMessageToIFrame(BYME_MESSAGES.LOGIN, {
        token: this.byMeApiToken,
        distributionID: this.distribId,
        additionalInfo: null,
      });
    }
  }

  postSettingsToIFrame() {
    if (this.projectToInit) {
      this.projectToInit = null;
    }

    this.postMessageToIFrame(
      BYME_MESSAGES.SET_UP_SETTINGS,
      this.iframeParametersAtInit
    );
  }

  handleIFrameMessage = async (e) => {
    if (
      (e.origin !== environment.byMe.whiteListUrl &&
        e.origin !== environment.byMe.whiteListUrlBathroom) ||
      !e.data
    ) {
      return;
    }

    if (!environment.production) {
      console.log(
        "%cwindow.handleIFrameMessage()",
        "background-color:purple;color:white",
        "byme -> simphonis",
        e
      );
    }

    switch (e.data.event) {
      case BYME_MESSAGES.AUTHENTICATION_REQUIRED: {
        let hbmIdToRequire: string = null;
        if (this.customer.userDatas) {
          if (
            !this.customer.userDatas.vendor ||
            this.customer.userDatas.otherAccount
          ) {
            //si client ou vendeur sur un compte client
            hbmIdToRequire = this.customer.userDatas.otherAccount
              ? this.customer.userDatas.otherAccount.hbmId
              : this.customer.userDatas.hbmId;
          }
        }

        this.getAuthParams(hbmIdToRequire)
          .then((authParams) => {
            this.postMessageToIFrame(BYME_MESSAGES.AUTHENTICATION, authParams);
          })
          .catch(() => {
            this.postMessageToIFrame(BYME_MESSAGES.AUTHENTICATION_FAILED);
          });
        break;
      }
      case BYME_MESSAGES.AUTHENTICATION_SUCCEEDED:
        break;

      case BYME_MESSAGES.CHANGE_NEED_MAPPING:
        this.router.navigate(["/design"]);
        break;

      case BYME_MESSAGES.CLOSE_APP:
        if (this.customer.initContext) {
          this.customer.context = this.customer.initContext;
        }
        this.router.navigate(["/"]);
        break;

      case BYME_MESSAGES.COMPOSER_PRICE_INFO_BUTTON_CLICKED:
        break;

      case BYME_MESSAGES.LOGIN_REQUIRED:
        //on demande au vendeur s'il veut enregistrer sur son compte ou un autre
        if (
          this.customer.userDatas.vendor &&
          !this.customer.userDatas.otherAccount
        ) {
          this.modalService.display(MODAL.PROMPT, {
            contentRef: "popins.switchAccount.save",
            buttons: [
              {
                labelRef: "popins.switchAccount.myAccount",
                action: () => {
                  this.modalService.close(); //cas sur mon compte
                  //si hbm_user_id présent, on se connecte
                  if (this.customer.userDatas.hbmId) {
                    this.acquireToken(this.customer.userDatas.hbmId, true);
                  } else {
                    //si hbm_user_id vide, le vendeur n'a pas de compte magento
                    this.magento.postMessageToMagento(
                      MAGENTO_MESSAGES.LOGIN_REQUIRED
                    );
                  }
                },
              },
              {
                //cas sur un autre compte : on ouvre la modale de recherche de compte
                labelRef: "popins.switchAccount.anotherAccount",
                action: () => {
                  this.modalService.close();
                  this.modalService.display(MODAL.SEARCH_CUSTOMER, {
                    onCustomerSelect: (
                      hbmId: string,
                      magentoId: string,
                      name: string
                    ) => {
                      this.modalService.close();
                      if (this.customer.userDatas.magentoId != magentoId) {
                        //on vérifie que le vendeur ne s'est pas choisi lui même
                        this.customer.userDatas.otherAccount = {
                          hbmId: hbmId,
                          completeName: name,
                          magentoId: magentoId,
                        };
                      }
                    },
                  });
                },
              },
            ],
          });
        } else {
          // Emit message to magento for LoginRequired
          this.magento.postMessageToMagento(MAGENTO_MESSAGES.LOGIN_REQUIRED);
        }
        break;

      case BYME_MESSAGES.LOGIN_SUCCEEDED:
        //cas login avant save
        if (this.loginForSaveRequested) {
          this.loginForSaveRequested = false;
          this.saveForAccountCreationRequested = true;
          this.postMessageToIFrame(BYME_MESSAGES.SAVE_REQUESTED);
        }
        break;

      case BYME_MESSAGES.LOGIN_FAILED:
        break;

      case BYME_MESSAGES.PLANS_2D_STOP:
        this._plan2DrenderingEventEmitter.emit();
        break;

      case BYME_MESSAGES.PRICE_INFO_BUTTON_CLICKED:
        this.modalService.display(MODAL.PRICE_INFOS, {
          bom: e.data.content,
          projectName: this.current.project ? this.current.project.name : null,
        });
        break;

      case BYME_MESSAGES.PRINT_BUTTON_CLICKED:
        if (this.customer.isLogged && this.current.project) {
          this.generateSnapshotAndPlans2d(true).then(() => {
            this.modalService.display(MODAL.PRINT_MEDIAS, {});
          });
        } else {
          this.checkForLoguedCustomer().then(() => {
            this.postMessageToIFrame(BYME_MESSAGES.SAVE_REQUESTED, {
              action: "print",
            });
          });
        }

        break;
      case BYME_MESSAGES.SHARE_PROJECT:
        if (this.current.project) {
          //on vérifie que le projet a été sauvegardé au moins une fois
          this.modalService.display(MODAL.SHARE, {
            rootUrl: this.magento.origin,
            id: e.data.content.projectID,
            image: e.data.content.image,
          });
        } else {
          this.modalService.display(MODAL.PROMPT, {
            titleRef: "popins.share.warningTitle",
            contentRef: "popins.share.warningContent",
            buttons: [
              {
                labelRef: "generic.ok",
                action: () => {
                  this.modalService.close();
                },
              },
            ],
          });
        }
        break;
      case BYME_MESSAGES.SHARE_PROJECT_BOM:
      case BYME_MESSAGES.PRODUCT_LIST_WITH_BOM: {
        this.bom = BomDataHelper.extractFlatProductsListWithDecos(e.data.content);        
        let beautifiedBom = BomDataHelper.extractBlockGroupedProductsList(
          e.data.content
        );
        this.datas
          .getStockAvailability(
            [].concat(
              ...beautifiedBom.blocks.map((block) =>
                block.rows.map((row) => row.reference)
              )
            )
          )
          .then((stocks: Stock[]) => {
            for (const product of this.bom.products) {
              const stockItem = stocks.filter(
                (s) => s.ean === product.reference
              );
              if (stockItem.length > 0) {
                product.stockAvailability = stockItem.map((item) => ({
                  storeId: item.store.code,
                  storeName: item.store.name,
                  stock: item.value,
                }));
              } else {
                product.stockAvailability = [];
              }
            }
            this.modalService.display(MODAL.PRODUCTS_LIST, {
              bom: beautifiedBom,
              flattenBom: this.bom,
            });
          });
        break;
      }
      case BYME_MESSAGES.PRODUCT_LIST_WITH_BOM_FORMATTED_PRICES:
        break;

      case BYME_MESSAGES.PROJECT_SAVED_REQUEST_FOR_PRINT:
        this._saveForPrintRequested = true;
        break;

      case BYME_MESSAGES.READY:
        this.onIFrameReady();
        break;

      case BYME_MESSAGES.SAVE_SUCCEEDED: {
        try {
          const projectService = this.injector.get(ProjectService);
          const projectData: any = await projectService.getDetails(
            e.data.content.id,
            this.customer.applicationId
          );
          let projectDuplicate: ProjectDuplicate = await this.datas.getProjectDuplicate(projectData.projectID);

          this.current.project = {
            description: projectData.description,
            name: projectData.projectName,
            id: projectData.projectID,
            uuid: projectData.projectUUID,
            originalId: projectDuplicate?.originalProjectId ?? projectData.projectID
          };

          const project_amount: number = projectData.totalPrice
            ? projectData.totalPrice.toFixed(2)
            : null;

          this.gtm.trackEvent("click", "project", "save", {
            project_amount: project_amount,
          });
          // Envoie de la donnée sur un projet sauvegardé
          this.eventDataTransfert.transmitData("save", project_amount);

          if (this._saveForPrintRequested) {
            this._saveForPrintRequested = false;
            await this.generateSnapshotAndPlans2d(true);
            this.modalService.display(MODAL.PRINT_MEDIAS, {});
          }

          //cas sauvegarde avant commande/panier
          if (this.saveForOrderRequested) {
            this.saveForOrderRequested = false;

            //il faut verrouiller le projet
            await projectService.lock(this.current.project.id);
            this.current.project.locked = true;
            //envoi du message de commande à magento
            this.magento.postMessageToMagento(MAGENTO_MESSAGES.ORDER_BOM, {
              hbm_user_id: this.customer.userDatas.hbmId,
              session_id: this.current.sessionId,
              project_id: this.current.project.id,
              project_type: this.customer.context,
              BOM: this.bom,
              role: this.customer.userDatas.vendor ? "vendor" : "customer",
            });

            // On envoie les donnees pour signaler qu'une commande a ete faite
            this.eventDataTransfert.transmitData("order", project_amount);
          }

          //cas sauvegarde avant création de compte magento
          if (this.saveForAccountCreationRequested) {
            this.saveForAccountCreationRequested = false;
            this.magento.postMessageToMagento(
              MAGENTO_MESSAGES.ACCOUNT_CREATED,
              { hbm_user_id: this.customer.userDatas.hbmId }
            );
          }
        } catch (ex) {
          console.error(ex);
        }
        break;
      }
      case BYME_MESSAGES.SNAPSHOT_GENERATED:
        break;
      case BYME_MESSAGES.SPECIAL_PRODUCT_INFO_REQUESTED:
        break;

      case BYME_MESSAGES.TOKEN_EXPIRED: {
        let hbmIdForExpiredToken: string = null;
        if (this.customer.userDatas) {
          if (
            !this.customer.userDatas.vendor ||
            this.customer.userDatas.otherAccount
          ) {
            hbmIdForExpiredToken = this.customer.userDatas.otherAccount
              ? this.customer.userDatas.otherAccount.hbmId
              : this.customer.userDatas.hbmId;
          }
        }

        this.getAuthParams(hbmIdForExpiredToken, true)
          .then((authParams) => {
            this.postMessageToIFrame(BYME_MESSAGES.TOKEN_REFRESHED, authParams);
          })
          .catch(() => {
            this.postMessageToIFrame(BYME_MESSAGES.AUTHENTICATION_FAILED);
          });
        break;
      }
      case BYME_MESSAGES.WALL_EDGE_STRIP_PRICE_INFO_BUTTON_CLICKED:
        break;
      case BYME_MESSAGES.WALL_PANEL_PRICE_INFO_BUTTON_CLICKED:
        break;
      case BYME_MESSAGES.WORKTOP_PRICE_INFO_BUTTON_CLICKED:
        break;
      case BYME_MESSAGES.WORKTOP_TYPES_INTO_REQUESTED:
        break;
      default:
        return;
    }
  };

  checkForLoguedCustomer(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.customer.isLogged) {
        resolve();
      } else if (this.customer.userDatas?.vendor) {
        this.modalService.display(MODAL.SEARCH_CUSTOMER, {
          onCustomerSelect: () => {
            this.modalService.close();
            resolve();
          },
        });
      }
    });
  }

  postMessageToIFrame(event, data?) {
    if (this.iframe) {
      if (!environment.production) {
        console.log(
          "%cplanner.postMessageToIFrame()",
          "background-color:orange;color:white",
          "simphonis -> byme",
          event,
          data
        );
      }
      this.iframe.contentWindow.postMessage(
        {
          event: event,
          content: data,
        },
        this.customer.context === CONTEXT.BATHROOM
          ? environment.byMe.whiteListUrlBathroom
          : environment.byMe.whiteListUrl
      );
    }
  }

  handlePrint() {
    if (this.customer.isLogged && this.current.project) {
      this.generateSnapshotAndPlans2d(true).then(() => {
        this.modalService.display(MODAL.PRINT_MEDIAS, {});
      });
    } else {
      this._saveForPrintRequested = true;
      this.postMessageToIFrame(BYME_MESSAGES.SAVE_REQUESTED, {
        from: "bryo",
        action: "print",
      });
    }
  }

  generateSnapshotAndPlans2d(withMeasure: boolean): Promise<void> {
    return new Promise((resolve) => {
      this.modalService.isLoading = true;

      let _s: Subscription = this._plan2DrenderingEventEmitter.subscribe(() => {
        this.modalService.isLoading = false;
        resolve();
        _s.unsubscribe();
      });

      this.postMessageToIFrame(BYME_MESSAGES.SNAPSHOT_REQUESTED, {});
      this.postMessageToIFrame(BYME_MESSAGES.COMPUTE_2D_PLANS, {
        version: 1,
        topPlans: 1,
        facePlans: 1,
        worktopPlans: 1,
        wallpanelPlans: 1,
        saveWithProject: 1,
        showFurnitureDimension: withMeasure ? 1 : 0,
        showWallDimension: withMeasure ? 1 : 0,
        showNumbering: 1,
        resol: 300,
        scale: 20,
        imageWidth: 2244,
        imageHeight: 1772,
      });
    });
  }

  getByMeDefaultSettings(): any {
    return {
      designFromScratch: true,
      name: "",
      templateTags: [
        {
          required: ["L", "priority"],
        },
        {
          required: ["L"],
          excluded: ["priority"],
        },
        {
          required: ["I", "priority"],
        },
        {
          required: ["I"],
          excluded: ["priority"],
        },
        {
          required: ["II", "priority"],
        },
        {
          required: ["II"],
          excluded: ["priority"],
        },
        {
          required: ["U", "priority"],
        },
        {
          required: ["U"],
          excluded: ["priority"],
        },
      ],
      nbMaxResultPerGroup: 5,
      iframeVersion: environment.byMe.iframeVersion,
    };
  }

  generateBymeApiRequest(params: any): Observable<any> {
    let _options: any = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        timestamp: Tools.generateFormatedTimestamp(),
        token: this.byMeApiToken,
      }),
    };

    if (params.responseType) {
      _options.responseType = params.responseType;
    }

    switch (params.method) {
      case "GET":
        return this.http.get(params.url, _options);
      case "POST":
        return this.http.post(params.url, params.body, _options);
      case "PUT":
        return this.http.put(params.url, params.body, _options);
      case "DELETE":
        return this.http.delete(params.url, _options);
      default:
        break;
    }
  }

  callBymeApi(params: any) {
    return new Promise((resolve, reject) => {
      this.generateBymeApiRequest(params).subscribe(
        (response: any) => {
          resolve(response);
        },
        (errorResponse: any) => {
          console.error(errorResponse);
          if (
            errorResponse.status >= HttpStatus.StatusCodes.BAD_REQUEST &&
            errorResponse.status <= HttpStatus.StatusCodes.INTERNAL_SERVER_ERROR
          ) {
            console.log("400 >= status <= 500");
            try {
              if (errorResponse.error.id === "BM_005") {
                console.log("token expiré");

                this.acquireToken(this.customer.userDatas.hbmId).then(
                  () => {
                    console.log("token renouvelé");
                    this.generateBymeApiRequest(params).subscribe(
                      (response: any) => {
                        console.log("_requestObservable2.responseHandler");
                        resolve(response);
                      },
                      (errorResponse: any) => {
                        console.log(
                          "_requestObservable2.errorResponse",
                          errorResponse
                        );
                        reject(errorResponse);
                      }
                    );
                  },
                  (acquireTokenError: any) => {
                    console.log("acquireTokenError", acquireTokenError);
                    reject(acquireTokenError);
                  }
                );
              } else {
                return reject(errorResponse);
              }
            } catch (error) {
              return reject(error);
            }
          }
        }
      );
    });
  }

  /**
   * Récupération d'un token user ou legal
   * @param userID
   * @param emitEvent
   * @returns
   */
  acquireToken(
    userID: string = null,
    emitEvent: boolean = true
  ): Promise<void> {
    let paramString: string = "";
    let params = new URLSearchParams();
    let headers = new HttpHeaders()
      .set("context", this.customer.context)
      .set("country", this.customer.country)
      .set("Content-Type", "application/json");

    if (userID) {
      params.set("userId", userID);
      paramString = "?" + params.toString();
    }

    return new Promise((resolve, reject) => {
      let params: any = {
        method: "GET",
        url:
          environment.backEnd.baseApi +
          environment.backEnd.byMe.apiName +
          "Token" +
          paramString,
        headers: headers,
      };

      this.datas.generateBackendApiRequest(params).subscribe(
        (tokenResponse: any) => {
          if (tokenResponse.token) {
            this.byMeApiToken = tokenResponse.token;
            this.byMeApiTokenType = userID ? "User" : "Legal";

            resolve();

            if (userID && this.iframe) {
              if (userID) {
                if (emitEvent) {
                  this.postMessageToIFrame(BYME_MESSAGES.LOGIN, {
                    token: tokenResponse.token,
                    distributionID: this.distribId,
                    additionalInfo: {},
                  });
                }
              }
            }
          } else {
            reject(new Error("Unknown error while acquiring token"));
          }
        },
        (tokenError: any) => {
          reject(tokenError);
        }
      );
    });
  }

  /**
   * Création d'un user
   * @returns
   */
  createUser(otherAccount: boolean): Promise<string> {
    return new Promise((resolve, reject) => {
      let headers = new HttpHeaders().set("context", this.customer.context);

      let params: any = {
        method: "POST",
        url:
          environment.backEnd.baseApi +
          environment.backEnd.byMe.apiName +
          "User",
        headers: headers,
      };

      this.datas.generateBackendApiRequest(params).subscribe(
        (user: any) => {
          if (otherAccount) {
            this.customer.userDatas.otherAccount = { hbmId: user.id };
          } else {
            this.customer.userDatas.hbmId = user.id;
          }
          resolve(user.id);
        },
        (errorResponse: any) => {
          console.log("createUser.errorResponse", errorResponse);
          reject(errorResponse);
        }
      );
    });
  }
}
